<template>
	<div
		id="status"
		:class="{ animate: animateView }"
		:style="{ 'animation-delay': animationDelay }"
		class="content-container"
	>
		<section
			id="assignment"
			class="section-container"
			:style="{ 'animation-delay': animationDelay }"
		>
			<div style="height: 52px; overflow: hidden">
				<div class="section-header clipped-medium-backward-events-logs">
					<img src="/icons/deployable.svg" />
					<h1>Current Assignment</h1>
				</div>
				<div class="rhombus-back">&nbsp;</div>
			</div>
			<div ref="assignment" class="section-content-container">
				<vue-markdown-it
					:source="missionMarkdown"
					preset="default"
					:options="{ html: true }"
					class="markdown"
				/>
			</div>
		</section>
		<!-- One full-height panel; the header tabs switch between reserves and clocks -->
		<div class="status-side">
			<section
				id="side-panel"
				class="section-container"
				:style="{ 'animation-delay': animationDelay }"
			>
				<div class="section-header clipped-medium-backward side-tabs" role="tablist">
					<button
						v-for="tab in tabs"
						:key="tab.id"
						role="tab"
						class="side-tab"
						:class="{ active: sideTab === tab.id }"
						:aria-selected="sideTab === tab.id"
						@click="setSideTab(tab.id)"
					>
						<img :src="tab.icon" />
						<h1>{{ tab.label }}</h1>
					</button>
				</div>
				<div ref="sideBody" class="section-content-container">
					<template v-if="sideTab === 'reserves'">
						<ReservesPanel v-if="reserves.length" :reserves="reserves" />
						<div v-else class="detail-empty">
							<p>// NO RESERVES //</p>
						</div>
					</template>
					<PilotStatusPanel v-else-if="sideTab === 'pilots'" :pilots="pilots" />
					<template v-else>
						<div v-if="clocks.length" class="clocks-list-container">
							<Clock
								v-for="item in clocks"
								:key="item.name"
								:clock="item"
								:animate="animate"
								:animation-delay="clockAnimationDelay"
							/>
						</div>
						<div v-else class="detail-empty">
							<p>// NO CLOCKS //</p>
						</div>
					</template>
				</div>
			</section>
		</div>
	</div>
</template>

<script>
import { VueMarkdownIt } from "@f3ve/vue-markdown-it";
import Clock from "@/components/Clock.vue";
import { retrieveRows, fillPips, countUp } from "@/utils/retrieve";
import ReservesPanel from "@/components/ReservesPanel.vue";
import PilotStatusPanel from "@/components/PilotStatusPanel.vue";

export default {
	components: {
		VueMarkdownIt,
		Clock,
		ReservesPanel,
		PilotStatusPanel,
	},
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
		initialSlug: {
			type: String,
			required: true,
		},
		missions: {
			type: Array,
			required: true,
		},
		pilots: {
			type: Array,
			required: true,
		},
		clocks: {
			type: Array,
			required: true,
		},
		reserves: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			animateView: this.animate,
			animationDelay: "1.75s",
			clockAnimationDelay: "2500",
			sideTab: "reserves",
		};
	},
	computed: {
		tabs() {
			return [
				{ id: "reserves", label: "Reserves", icon: "/icons/squad.svg" },
				{ id: "clocks", label: "Clocks", icon: "/icons/protocol.svg" },
				{ id: "pilots", label: "Pilots", icon: "/icons/pilot.svg" },
			];
		},
		currentMission() {
			// The mission marked "start" is the live briefing; initialSlug is only a fallback.
			return (
				this.missions.find(m => m.status === "start") ||
				this.missions.find(m => m.slug === this.initialSlug) ||
				this.missions[0]
			);
		},
		missionMarkdown() {
			return this.currentMission ? this.currentMission.content : "";
		},
	},
	created() {
		try {
			const saved = localStorage.getItem("statusSideTab");
			if (["reserves", "clocks", "pilots"].includes(saved)) this.sideTab = saved;
		} catch (e) {
			/* storage unavailable */
		}
		this.setAnimate();
		this.setClockAnimateDelay();
	},
	mounted() {
		if (this.animate) this.$nextTick(() => this.loadSideRows(0.8));
	},
	watch: {
		// Missions load asynchronously: type the briefing in once it arrives
		missionMarkdown: {
			immediate: true,
			handler(text) {
				if (!text || this.briefingShown) return;
				this.briefingShown = true;
				this.$nextTick(() => this.typeBriefing());
			},
		},
	},
	methods: {
		setSideTab(tab) {
			if (tab === this.sideTab) return;
			this.sideTab = tab;
			this.$nextTick(() => this.loadSideRows(0));
			// Per-viewer convenience: reopen the same tab next visit
			try {
				localStorage.setItem("statusSideTab", tab);
			} catch (e) {
				/* storage unavailable */
			}
		},
		// The side tab's rows load in one by one, on page load and on every switch.
		// Pilots are few and tall, so they come in slower than reserve rows
		// The briefing scrolls in line by line, left to right, like a teletype
		typeBriefing() {
			const box = this.$refs.assignment;
			if (!box || !this.animate) return;
			const lines = box.querySelectorAll(".markdown > :not(ul):not(ol), .markdown li");
			// bleed: bullets sit left of each list item's box; include them in the wipe
			retrieveRows(lines, { startDelay: 0.6, gap: 0.22, duration: 0.55, bleed: 40 });
		},
		loadSideRows(startDelay) {
			const body = this.$refs.sideBody;
			if (!body) return;
			const rows = body.querySelectorAll(
				".reserve-group-header, .grouped-row, .pilot-status, .clock-container, .detail-empty"
			);
			// Each pilot wipes in fully; the next starts as the previous one nears the end.
			// Once a pilot is in, its stress boxes and XP pips fill and the XP counts up
			if (this.sideTab === "pilots") {
				const gap = 0.55;
				retrieveRows(rows, { startDelay, gap, duration: 0.7 });
				[...rows].forEach((row, i) => {
					const at = startDelay + i * gap + 0.45;
					fillPips(row.querySelectorAll(".ps-box.filled"), { delay: at, gap: 0.09 });
					fillPips(row.querySelectorAll(".ps-pip.filled"), {
						delay: at + 0.1,
						gap: 0.07,
						empty: "rgba(0, 0, 0, 0)",
					});
					const xp = row.querySelector(".ps-xp-value");
					if (xp) countUp(xp, Number(xp.textContent) || 0, { delay: at + 0.1 });
				});
			} else retrieveRows(rows, { startDelay, gap: 0.05, duration: 0.5 });
		},
		setAnimate() {
			if (this.animate) {
				this.animateView = true;
			}
			let terminalExit = window.sessionStorage.getItem("terminalExit");
			if (terminalExit) {
				this.animationDelay = "3s";
				window.sessionStorage.removeItem("terminalExit");
				return;
			}
			let statusAnimated = window.sessionStorage.getItem("statusAnimated");
			if (statusAnimated) {
				this.animationDelay = "0s";
			}
			if (statusAnimated === null) {
				window.sessionStorage.setItem("statusAnimated", true);
			}
		},
		setClockAnimateDelay() {
			let delayToFloat = parseFloat(this.animationDelay.replace("s", ""));
			let finalClockDelay = delayToFloat * 600 + 600;
			this.clockAnimationDelay = finalClockDelay.toString();
		},
	},
};
</script>

<style scoped>
.side-tabs {
	gap: 0;
	padding-left: 6px;
}

.side-tab {
	display: flex;
	align-items: center;
	gap: 10px;
	height: 100%;
	padding: 0 14px 0 10px;
	background: none;
	border: none;
	border-bottom: 3px solid transparent;
	color: var(--text-headers);
	opacity: 0.45;
	cursor: pointer;
	transition: opacity 0.15s, border-color 0.15s;
}

.side-tab:hover {
	opacity: 0.75;
}

.side-tab.active {
	opacity: 1;
	border-bottom-color: var(--text-headers);
}

.side-tab img {
	width: 30px;
	height: 30px;
}

/* Narrow columns: tighter tabs so all three fit inside the clipped header */
@media (max-width: 1500px) {
	.side-tab {
		gap: 6px;
		padding: 0 8px 0 6px;
	}

	.side-tab img {
		width: 22px;
		height: 22px;
	}

	.side-tab h1 {
		font-size: 20px !important;
	}
}

/* Phones: labels only */
@media (max-width: 768px) {
	.side-tab img {
		display: none;
	}

	.side-tab h1 {
		font-size: 18px !important;
	}
}

.side-tab h1 {
	margin: 0;
}
</style>
